import Container from '@risk-smart/themed-cloudscape-components/container';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useDeleteAssessmentActivities } from '@/hooks/mutations/assessment-activity/useDeleteAssessmentActivities';
import { useGetOversightAssessmentActivitiesRegister } from '@/hooks/queries';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';

import { useGetCollectionTableProps } from '../update/tabs/activities/activtiesConfig';
import type { AssessmentActivityFields } from '../update/tabs/activities/types';
import { useGetDefaultRibbonFilters } from './defaultRibbonFilters';

type ActivityItemType = AssessmentActivityFields;

const Page: FC = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'oversightAssessmentActivities',
  });
  const { t: st } = useTranslation(['common']);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState<
    ActivityItemType[]
  >([]);

  const {
    hasPermission: canDeleteAssessmentActivities,
    loading: isLoadingDeleteAssessmentActivities,
  } = useHasPermissionQuery(`delete:assessment_activity`);

  const { data, loading } = useGetOversightAssessmentActivitiesRegister({
    queryArgs: {},
  });

  const { deleteAssessmentActivities, loading: deleteLoading } =
    useDeleteAssessmentActivities();

  const { t: tt } = useTranslation(['taxonomy']);

  const filteredActivities = useMemo(
    () => data?.assessment_activity.filter((activity) => !activity.IsRCSA),
    [data]
  );

  const activitiesTableProps = useGetCollectionTableProps(
    'compliance_monitoring_assessment',
    filteredActivities,
    tt('oversight_activity')
  );

  const activitiesCount = useMemo(() => {
    if (loading) {
      return '';
    }

    return `(${filteredActivities?.length ?? 0})`;
  }, [loading, filteredActivities]);

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    asyncAction: async () => {
      await deleteAssessmentActivities(selectedActivities.map((s) => s.Id));
      setSelectedActivities([]);
      setIsDeleteModalVisible(false);

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const buttons = [];

  if (!isLoadingDeleteAssessmentActivities && canDeleteAssessmentActivities) {
    buttons.push({
      text: t('delete_activity_button'),
      id: 'deleteActivityButton',
      onItemClick: () => {
        setIsDeleteModalVisible(true);
      },
      disabled: selectedActivities.length === 0,
    });
  }

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const title = st('navigationMenu.assessments.oversightActivitiesTitle');

  return (
    <PageLayout
      title={title}
      counter={activitiesCount}
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={activitiesTableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <ActionsButton
            key={'actions'}
            buttonText={st('actionsButton')}
            items={buttons}
          />
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={activitiesTableProps.allItems}
        propertyFilterQuery={activitiesTableProps.propertyFilterQuery}
        onFilterQueryChanged={activitiesTableProps.actions.setPropertyFiltering}
        filteringProperties={activitiesTableProps.filteringProperties}
        filteringOptions={
          activitiesTableProps.propertyFilterProps.filteringOptions
        }
        parentType={ParentTypes.AssessmentActivity}
        {...ribbonProps}
      />
      <Container variant={'default'}>
        <Table
          {...activitiesTableProps}
          selectionType={
            !isLoadingDeleteAssessmentActivities &&
            canDeleteAssessmentActivities
              ? 'multi'
              : undefined
          }
          selectedItems={selectedActivities}
          trackBy={'Id'}
          onSelectionChange={({ detail }) => {
            setSelectedActivities(detail.selectedItems);
          }}
          variant={'embedded'}
          loading={loading}
        />
      </Container>
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {t('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
